// Mock Data for Event Management Platform Landing Page

export const features = [
  {
    id: 1,
    title: "Event Creation & Management",
    description: "Create and manage multiple events with ease. Set up event details, schedules, and configurations all in one place.",
    icon: "Calendar",
    color: "#283750"
  },
  {
    id: 2,
    title: "Smart Ticketing System",
    description: "Flexible ticketing with multiple tiers, early bird pricing, and automated ticket delivery to attendees.",
    icon: "Ticket",
    color: "#fad24b"
  },
  {
    id: 3,
    title: "Attendee Management",
    description: "Comprehensive attendee tracking, registration management, and real-time attendance monitoring.",
    icon: "Users",
    color: "#3dd3ee"
  },
  {
    id: 4,
    title: "Check-in Management",
    description: "Fast and efficient check-in process with QR code scanning, mobile check-in, and real-time updates.",
    icon: "QrCode",
    color: "#ff8c19"
  },
  {
    id: 5,
    title: "Badge Printing",
    description: "Instant badge printing with custom designs, attendee information, and QR codes for seamless event experience.",
    icon: "IdCard",
    color: "#b4dc19"
  },
  {
    id: 6,
    title: "Email Marketing",
    description: "Powerful email blast capabilities with template builders, scheduling, and analytics to reach your audience.",
    icon: "Mail",
    color: "#fa551e"
  },
  {
    id: 7,
    title: "Form Builder",
    description: "Create custom registration forms, surveys, and feedback forms with drag-and-drop simplicity.",
    icon: "FileText",
    color: "#78286e"
  },
  {
    id: 8,
    title: "Analytics Dashboard",
    description: "Real-time insights into ticket sales, attendance, engagement, and revenue with beautiful visualizations.",
    icon: "BarChart3",
    color: "#000"
  },
  {
    id: 9,
    title: "Screen Management",
    description: "Manage event screens, displays, and digital signage with real-time content updates and scheduling.",
    icon: "Monitor",
    color: "#283750"
  }
];

export const howItWorksSteps = [
  {
    step: 1,
    title: "Create Your Event",
    description: "Set up your event details, dates, venue information, and customize your event page in minutes.",
    image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87"
  },
  {
    step: 2,
    title: "Configure Ticketing",
    description: "Set up ticket types, pricing tiers, early bird discounts, and payment gateways with just a few clicks.",
    image: "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04"
  },
  {
    step: 3,
    title: "Promote & Sell",
    description: "Use our email marketing tools, social media integrations, and promotional features to reach your audience.",
    image: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678"
  },
  {
    step: 4,
    title: "Manage On Event Day",
    description: "Check-in attendees, print badges, manage screens, and monitor everything in real-time from your dashboard.",
    image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30"
  }
];

export const pricingPlans = [
  {
    id: 1,
    name: "Starter",
    price: "500",
    period: "month",
    description: "Perfect for small events and occasional organizers",
    features: [
      "Up to 3 events per month",
      "500 attendees per event",
      "Basic ticketing system",
      "Email support",
      "Standard check-in",
      "Basic analytics"
    ],
    highlighted: false,
    color: "#283750"
  },
  {
    id: 2,
    name: "Professional",
    price: "800",
    period: "month",
    description: "Ideal for professional event organizers",
    features: [
      "Unlimited events",
      "1,000 attendees per event",
      "Advanced ticketing & pricing",
      "Priority support",
      "Badge printing",
      "Email marketing suite",
      "Custom branding",
      "Advanced analytics"
    ],
    highlighted: true,
    color: "#000"
  },
  {
    id: 3,
    name: "Enterprise",
    price: "Custom",
    period: "contact us",
    description: "For large-scale events and organizations",
    features: [
      "Unlimited everything",
      "Dedicated account manager",
      "Custom integrations",
      "White-label solution",
      "24/7 phone support",
      "Custom development",
      "Advanced security",
      "SLA guarantee"
    ],
    highlighted: false,
    color: "#3dd3ee"
  }
];

export const testimonials = [
  {
    id: 1,
    name: "Sarah Johnson",
    role: "Event Director",
    company: "Tech Summit Global",
    content: "This platform transformed how we manage our annual tech summit. The check-in process is 10x faster and attendee satisfaction has skyrocketed.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1581065178047-8ee15951ede6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc2MzkwNjY4OXww&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 2,
    name: "Michael Chen",
    role: "Marketing Manager",
    company: "Corporate Events Inc",
    content: "The email marketing and analytics features are outstanding. We've seen a 40% increase in ticket sales since switching to this platform.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NjM5MDY3MDJ8MA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 3,
    name: "Emily Rodriguez",
    role: "Conference Organizer",
    company: "Global Conferences Network",
    content: "Badge printing and attendee management features are game-changers. Everything is so seamless and professional.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NjM5MDY3MDJ8MA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 4,
    name: "David Park",
    role: "Operations Lead",
    company: "Festival Management Co",
    content: "Managing large festivals has never been easier. The real-time dashboard and staff management tools are exceptional.",
    rating: 5,
    image: "https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg"
  },
  {
    id: 5,
    name: "Jessica Williams",
    role: "Event Coordinator",
    company: "Professional Events Ltd",
    content: "The form builder is incredibly intuitive. We've created custom registration flows that perfectly match our brand.",
    rating: 5,
    image: "https://images.unsplash.com/photo-1762522921456-cdfe882d36c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc2MzkwNjY4OXww&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 6,
    name: "Robert Martinez",
    role: "CEO",
    company: "Event Solutions Pro",
    content: "Best investment we've made. The ROI is incredible and our clients love the professional experience we can now deliver.",
    rating: 5,
    image: "https://images.pexels.com/photos/34817083/pexels-photo-34817083.jpeg"
  }
];

export const stats = [
  {
    id: 1,
    value: "50K+",
    label: "Events Created",
    icon: "Calendar"
  },
  {
    id: 2,
    value: "2M+",
    label: "Attendees Managed",
    icon: "Users"
  },
  {
    id: 3,
    value: "98%",
    label: "Customer Satisfaction",
    icon: "Star"
  },
  {
    id: 4,
    value: "24/7",
    label: "Support Available",
    icon: "Headphones"
  }
];

export const dashboardFeatures = [
  {
    id: 1,
    title: "Real-Time Analytics",
    description: "Monitor ticket sales, attendance, and revenue in real-time",
    image: "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxhbmFseXRpY3MlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzYzODc0NjY0fDA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 2,
    title: "Interactive Reports",
    description: "Generate comprehensive reports with customizable metrics",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxhbmFseXRpY3MlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzYzODc0NjY0fDA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 3,
    title: "Data Visualization",
    description: "Beautiful charts and graphs to understand your event data",
    image: "https://images.pexels.com/photos/577195/pexels-photo-577195.jpeg"
  },
  {
    id: 4,
    title: "Geographic Insights",
    description: "See where your attendees are coming from with interactive maps",
    image: "https://images.pexels.com/photos/6366444/pexels-photo-6366444.jpeg"
  }
];
